// Helpers for the universal file registry (`FILES_API.md`) used by the HR app
// for employee photos and scanned personnel documents.

/** Storage folder slug used for employee documents. */
export const EMPLOYEE_DOCUMENTS_CATEGORY = 'employee-documents'

/** Largest file the API accepts (20 MB, `FileStorageService::MAX_FILE_BYTES`). */
export const MAX_FILE_BYTES = 20 * 1024 * 1024

/** Largest employee photo the API accepts (5 MB, `ImageStorageService`). */
export const MAX_IMAGE_BYTES = 5 * 1024 * 1024

const IMAGE_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']

function apiBaseUrl(): string {
  return String(import.meta.env.VITE_API_BASE_URL || '').trim()
}

/** Origin that serves the static `/uploads/...` files. */
function mediaBaseUrl(): string {
  const mediaBase = String(import.meta.env.VITE_MEDIA_BASE_URL || '').trim()
  if (mediaBase) return mediaBase.replace(/\/+$/, '')

  const base = apiBaseUrl()
  if (!base) return ''
  try {
    return new URL(base).origin
  } catch {
    return ''
  }
}

/**
 * Absolute URL for a stored employee photo (`/uploads/personnel/...`) or file.
 * Absolute, blob and data URLs are returned unchanged.
 */
export function resolveMediaUrl(value?: string | null): string {
  const raw = String(value || '').trim()
  if (!raw) return ''
  if (/^(?:https?:)?\/\//i.test(raw) || raw.startsWith('data:') || raw.startsWith('blob:')) return raw

  const base = mediaBaseUrl()
  if (!base) return raw
  return `${base}/${raw.replace(/^\.?\//, '')}`
}

/**
 * Path the shared API client can request for a stored file URL.
 * The client already carries the `/api/v1` prefix in its `baseURL`.
 */
export function toApiFilePath(url?: string | null): string {
  const raw = String(url || '').trim()
  if (!raw) return ''

  const downloadMatch = /\/files\/([^/?#]+)\/download/i.exec(raw)
  if (downloadMatch) return `/files/${downloadMatch[1]}/download`

  if (/^https?:\/\//i.test(raw)) {
    try {
      return new URL(raw).pathname.replace(/^\/api\/v\d+/i, '')
    } catch {
      return raw
    }
  }

  return raw.replace(/^\/api\/v\d+/i, '') || raw
}

/** Reads a picked file as the `data:<mime>;base64,...` payload the API expects. */
export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : ''
      if (!result) {
        reject(new Error('empty_file'))
        return
      }
      resolve(result)
    }
    reader.onerror = () => reject(reader.error ?? new Error('read_failed'))
    reader.readAsDataURL(file)
  })
}

/** `true` when the picked file is an image the photo endpoint accepts. */
export function isAllowedImage(file: File): boolean {
  return IMAGE_MIME_TYPES.includes(file.type)
}

/** Human readable file size (`1.4 MB`). */
export function formatFileSize(bytes: number | null | undefined): string {
  const value = Number(bytes ?? 0)
  if (!Number.isFinite(value) || value <= 0) return '0 B'
  if (value < 1024) return `${value} B`
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(0)} KB`
  return `${(value / (1024 * 1024)).toFixed(1)} MB`
}
